import { Currency, Money } from "@hubrise/react-components"
import _ from "lodash"

import { DealId, DealLineId, DealLineSkuId } from "shared/hubrise/ids"
import { DealLinePricing, ICatalog, IDeal, IDealLine } from "shared/hubrise/models"
import { removeInArrayById, replaceInArrayById } from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"

/**
 * Create an empty deal line.
 * TODO: pass [sku] as argument
 */
export function create(catalog: ICatalog): IDealLine {
  if (catalog.data.products.length === 0) throw "At least one product must exist to create a deal line"
  const randomProduct = catalog.data.products[0]
  const randomSku = randomProduct.skus[0]

  return {
    id: DealLineId(catalog.idGenerator()),
    label: "",
    pricingEffect: "unchanged",
    pricingValue: null,
    skus: [{ id: DealLineSkuId(catalog.idGenerator()), skuId: randomSku.id, extraCharge: null }],
  }
}

/**
 * Delete the deal line. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param dealId
 * @param dealLineId
 */
export function doDelete(catalog: ICatalog, dealId: DealId, dealLineId: DealLineId): ICatalog {
  return updatedCatalog(catalog, {
    deals: replaceInArrayById(catalog.data.deals, dealId, (deal) => ({
      ...deal,
      lines: removeInArrayById(deal.lines, dealLineId),
    })),
  })
}

/**
 * Return the deal line number. Numbers start from 1.
 * @param deal
 * @param dealLine
 * @param isNew
 */
export function number(deal: IDeal, dealLine: IDealLine, isNew = false): number {
  return isNew ? deal.lines.length + 1 : _.findIndex(deal.lines, (line) => line.id === dealLine.id) + 1
}

/**
 * Return the new pricing of a deal line, after switching its pricing effect.
 * When the type of the pricing value changes, the pricing value we return is calculated from the older value.
 * @param dealLine
 * @param pricingEffect
 * @param currency
 */
export function switchPricingEffect(
  dealLine: IDealLine,
  pricingEffect: DealLinePricing["pricingEffect"],
  currency: Currency,
): DealLinePricing {
  const asMoney = (pricing: DealLinePricing): Money => {
    switch (pricing.pricingEffect) {
      case "unchanged":
        return Money.zero(currency)
      case "price_off":
      case "fixed_price":
        return pricing.pricingValue
      case "percentage_off":
        return new Money(Math.round(pricing.pricingValue * 100), currency)
    }
  }

  const asPercentage = (pricing: DealLinePricing): number => {
    switch (pricing.pricingEffect) {
      case "unchanged":
        return 0
      case "price_off":
      case "fixed_price":
        return pricing.pricingValue.cents / 100
      case "percentage_off":
        return pricing.pricingValue
    }
  }

  switch (pricingEffect) {
    case "unchanged":
      return { pricingEffect, pricingValue: null }
    case "price_off":
    case "fixed_price":
      return { pricingEffect, pricingValue: asMoney(dealLine) }
    case "percentage_off":
      return { pricingEffect, pricingValue: asPercentage(dealLine) }
  }
}

/**
 * Update a field in a deal line.
 * @param catalog
 * @param dealId
 * @param dealLineId
 * @param delta
 */
export function update<T extends keyof IDealLine>(
  catalog: ICatalog,
  dealId: DealId,
  dealLineId: DealLineId,
  fieldName: T,
  value: IDealLine[T],
): ICatalog {
  return updatedCatalog(catalog, {
    deals: replaceInArrayById(catalog.data.deals, dealId, (deal) => ({
      ...deal,
      lines: replaceInArrayById(deal.lines, dealLineId, (dealLine) => ({ ...dealLine, [fieldName]: value })),
    })),
  })
}

/**
 * Update the pricing of a deal line.
 * @param catalog
 * @param dealId
 * @param dealLineId
 * @param pricing
 */
export function updatePricing(
  catalog: ICatalog,
  dealId: DealId,
  dealLineId: DealLineId,
  pricing: DealLinePricing,
): ICatalog {
  return updatedCatalog(catalog, {
    deals: replaceInArrayById(catalog.data.deals, dealId, (deal) => ({
      ...deal,
      lines: replaceInArrayById(deal.lines, dealLineId, (dealLine) => ({ ...dealLine, ...pricing })),
    })),
  })
}
